import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, Loader2, ChevronDown, ShieldCheck, ArrowLeft, MessageSquare, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { COUNTRIES, DEFAULT_COUNTRY, flagEmoji } from "@/lib/countries";
import { apiError } from "@/lib/api";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const CHANNELS = [["sms", "SMS", Phone], ["whatsapp", "WhatsApp", MessageSquare], ["email", "Email", Mail]];

export const PhoneOtpModal = () => {
  const { startOtp, verifyOtp } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("phone");
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [local, setLocal] = useState("");
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [channel, setChannel] = useState("sms");
  const [identifier, setIdentifier] = useState("");
  const [sentMsg, setSentMsg] = useState("");
  const codeRef = useRef(null);

  useEffect(() => {
    const openHandler = () => { setOpen(true); setStep("phone"); };
    window.addEventListener("open-auth", openHandler);
    return () => window.removeEventListener("open-auth", openHandler);
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const e164 = useMemo(() => `+${country.dial}${local.replace(/[^\d]/g, "")}`, [country, local]);
  const emailValid = EMAIL_RE.test(email.trim());
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.iso.toLowerCase() === q);
  }, [search]);

  const close = () => { setOpen(false); setLocal(""); setEmail(""); setEmailTouched(false); setCode(""); setStep("phone"); };

  const buildPayload = () => {
    if (channel === "email") return { channel, email: email.trim() };
    if (channel === "whatsapp") return { channel, phone: e164, email: email.trim() };
    return { channel, phone: e164 };
  };

  const validate = () => {
    if (channel !== "email" && local.replace(/[^\d]/g, "").length < 6) { toast.error("Enter a valid phone number"); return false; }
    if (channel === "sms" && country.dial !== "250") { toast.error("SMS OTP is available for Rwanda numbers only (+250)"); return false; }
    if ((channel === "whatsapp" || channel === "email") && !emailValid) { setEmailTouched(true); toast.error("Please enter a valid email address"); return false; }
    return true;
  };

  const send = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const data = await startOtp(buildPayload());
      setIdentifier(data?.identifier || e164);
      setSentMsg(data?.message || "Verification code sent");
      setStep("otp");
      setResendIn(30);
      setTimeout(() => codeRef.current?.focus(), 100);
      toast.success(data?.message || "Verification code sent");
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      await verifyOtp({ identifier, code });
      toast.success("Welcome to B&B KIGALI 🎙️");
      close();
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const PhoneField = (
    <>
      <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Phone number</label>
      <div className="mt-2 flex gap-2">
        <div className="relative">
          <button type="button" onClick={() => { setPickerOpen((o) => !o); setSearch(""); }} data-testid="country-select-btn"
            className="h-12 px-3 rounded-xl bg-[#0A0C10] border border-[#273042] flex items-center gap-1.5 hover:border-[#E11D2A]/60 transition-colors">
            <span className="text-xl leading-none">{flagEmoji(country.iso)}</span>
            <span className="text-white text-sm font-semibold">+{country.dial}</span>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </button>
          {pickerOpen && (
            <div className="absolute z-50 mt-2 w-72 max-h-80 rounded-xl bg-[#121620] border border-[#273042] shadow-2xl overflow-hidden">
              <div className="p-2 border-b border-[#273042] flex items-center gap-2">
                <Search className="w-4 h-4 text-slate-500" />
                <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} data-testid="country-search-input"
                  placeholder="Search country or code" className="w-full bg-transparent text-white text-sm outline-none placeholder:text-slate-500" />
              </div>
              <div className="overflow-y-auto max-h-64">
                {filtered.map((c) => (
                  <button key={c.iso} onClick={() => { setCountry(c); setPickerOpen(false); }} data-testid={`country-option-${c.iso}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#1A202C] text-left transition-colors">
                    <span className="text-lg">{flagEmoji(c.iso)}</span>
                    <span className="text-sm text-slate-200 flex-1 truncate">{c.name}</span>
                    <span className="text-xs text-slate-400 font-mono">+{c.dial}</span>
                  </button>
                ))}
                {filtered.length === 0 && <div className="px-3 py-6 text-center text-slate-500 text-sm">No match</div>}
              </div>
            </div>
          )}
        </div>
        <input value={local} onChange={(e) => setLocal(e.target.value)} inputMode="tel"
          onKeyDown={(e) => e.key === "Enter" && send()} data-testid="phone-number-input" placeholder="788 123 456"
          className="h-12 flex-1 min-w-0 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white outline-none focus:border-[#E11D2A]/60 transition-colors" />
      </div>
    </>
  );

  const EmailField = (
    <div className="mt-4">
      <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Email address</label>
      <input value={email} onChange={(e) => setEmail(e.target.value)} onBlur={() => setEmailTouched(true)} type="email" inputMode="email"
        onKeyDown={(e) => e.key === "Enter" && send()} data-testid="email-input" placeholder="you@example.com"
        className={`mt-2 w-full h-12 rounded-xl bg-[#0A0C10] border px-4 text-white outline-none transition-colors ${emailTouched && !emailValid ? "border-red-500/70 focus:border-red-500" : "border-[#273042] focus:border-[#E11D2A]/60"}`} />
      {emailTouched && !emailValid && <p className="mt-1.5 text-xs text-red-400" data-testid="email-error">Please enter a valid email address</p>}
    </div>
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="auth-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
          <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
            className="relative w-full max-w-md glass border border-[#273042] rounded-2xl p-7 shadow-2xl">
            <button onClick={close} data-testid="auth-close-btn" className="absolute right-4 top-4 text-slate-400 hover:text-white transition-colors"><X className="w-5 h-5" /></button>

            <div className="flex items-center gap-3 mb-6">
              <img src="/bb-logo.png" alt="B&B KIGALI" className="h-11 w-auto" />
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#E11D2A] leading-tight border-l border-[#273042] pl-3">Muri sports,<br/>ni igitego!</span>
            </div>

            {step === "phone" ? (
              <>
                <h2 className="text-2xl font-extrabold text-white">Sign in</h2>
                <p className="text-slate-400 text-sm mt-1 mb-4">Choose how you'd like to receive your 6-digit code.</p>

                <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Login with</label>
                <div className="mt-2 mb-4 grid grid-cols-3 gap-2">
                  {CHANNELS.map(([v, l, Icon]) => (
                    <button key={v} type="button" onClick={() => { setChannel(v); setEmailTouched(false); }} data-testid={`otp-channel-${v}`}
                      className={`h-12 rounded-xl border text-sm font-semibold transition-colors flex flex-col items-center justify-center gap-0.5 ${channel === v ? "border-[#E11D2A] bg-[#E11D2A]/10 text-white" : "border-[#273042] bg-[#0A0C10] text-slate-300 hover:border-[#E11D2A]/50"}`}>
                      <Icon className="w-4 h-4" /> {l}
                    </button>
                  ))}
                </div>

                {channel === "sms" && (
                  <>
                    <div className="mb-3 rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-xs text-amber-300" data-testid="sms-rwanda-notice">
                      SMS OTP is available for Rwanda numbers only (+250)
                    </div>
                    {PhoneField}
                  </>
                )}

                {channel === "whatsapp" && (
                  <>
                    {PhoneField}
                    <div className="mt-3 rounded-lg bg-[#121620] border border-[#273042] px-3 py-2 text-xs text-slate-300" data-testid="wa-email-notice">
                      Please enter your email address. OTP will be sent to both your WhatsApp and email.
                    </div>
                    {EmailField}
                  </>
                )}

                {channel === "email" && (
                  <>
                    <p className="text-xs text-slate-400 mb-1" data-testid="email-only-notice">Enter your email — your OTP will be sent there. No phone number required.</p>
                    {EmailField}
                  </>
                )}

                <button onClick={send} disabled={busy} data-testid="send-otp-btn"
                  className="mt-6 w-full h-12 rounded-xl bg-[#E11D2A] hover:bg-[#C8121F] text-white font-bold btn-glow flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send code"}
                </button>
                <p className="mt-4 text-[11px] text-slate-500 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Secure verification — SMS, WhatsApp or Email.</p>
              </>
            ) : (
              <>
                <button onClick={() => setStep("phone")} className="text-slate-400 hover:text-white flex items-center gap-1 text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
                <h2 className="text-2xl font-extrabold text-white">Enter code</h2>
                <p className="text-slate-400 text-sm mt-1 mb-6" data-testid="otp-sent-message">{sentMsg}</p>
                <input ref={codeRef} value={code} onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
                  onKeyDown={(e) => e.key === "Enter" && verify()} data-testid="otp-input-field" inputMode="numeric" placeholder="••••••"
                  className="w-full h-14 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-center text-2xl tracking-[0.5em] font-mono text-white outline-none focus:border-[#E11D2A]/60" />
                <button onClick={verify} disabled={busy} data-testid="verify-otp-btn"
                  className="mt-6 w-full h-12 rounded-xl bg-[#E11D2A] hover:bg-[#C8121F] text-white font-bold btn-glow flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify & continue"}
                </button>
                <div className="mt-4 text-center">
                  {resendIn > 0
                    ? <span className="text-sm text-slate-500">Resend code in {resendIn}s</span>
                    : <button onClick={send} data-testid="resend-otp-btn" className="text-sm text-[#E11D2A] hover:underline font-semibold">Resend code</button>}
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
